import { FileExporterUtilitiesService } from './file-exporter-utilities.service';

declare const XLSX;
declare const JSZip;

/**
  * A helper service with the ability to export files
  */
export class FileExporterService {
  constructor(private FileSaver, private fileExporterUtilitiesService: FileExporterUtilitiesService) {
  }

  /**
   * Downloads a zip file containing a single file containing fileData
   * @param zipFileName the name the zip file should have
   * @param fileName the name of the internal file including its extension
   * @param fileData the data to be put in the internal file
   */
  public downloadZipFile = (zipFileName: string, fileName: string, fileData: string) => {
    const zip = new JSZip();
    zip.file(fileName, fileData);
    this.downloadFile(zipFileName, zip.generate({ type: 'blob' }), '.zip');
  }

  /**
    * Downloads a file
    * @param {string} name The name of the file for user to download
    * @param {object} data The workbook data in a array buffer format
    * @param {string} fileType The file extension to download
    */
  public downloadFile(name: string, data: any, format: string) {
    let type = 'text/plain';
    let blob;

    if (format === 'csv') {
      type = 'text/csv';
      const BOM = "\uFEFF";
      const csvContents = BOM + data;
      blob = new Blob([csvContents], { type: `${type};charset=utf-8` });
    } else {
      blob = new Blob([data]);
    }

    this.FileSaver.saveAs(blob, name + '.' + format.toLowerCase());
  }

  /**
    * Exports excel files from a data stream
    * @param {ArrayBuffer} dataStream An object containing a series of bytes
    * @param {string} fileType The extension of the output file
    * @param {string} outputFileName The name of the output file
    */
  public exportToExcel(dataStream: ArrayBuffer, fileType: string, outputFileName: string) {
    const u8 = new Uint8Array(dataStream);
    const decoder = new TextDecoder('utf-8');


    if (fileType.toLowerCase() === 'csv') {
      this.downloadFile(outputFileName, decoder.decode(u8), fileType);
      return;
    }

    const arr = new Array();

    for (let i = 0; i !== u8.length; i++) {
      arr[i] = String.fromCharCode(u8[i]);
    }

    const data = arr.join('');
    this.downloadXlsxFile(outputFileName, data);
  }

  /**
   * Converts data to Xlsx format and downloads
   * @param {string} outputFileName The name of the file for user to download
   * @param {object} data The workbook data in a binary format
   */
  private downloadXlsxFile(outputFileName: string, data: any) {
    const workbook = XLSX.read(data, { type: 'binary' });
    let output;

    this.setAutoColumnWidths(workbook);
    output = XLSX.write(workbook, { bookType: 'xlsx', bookSST: true, type: 'binary' });

    this.downloadFile(
      outputFileName,
      this.fileExporterUtilitiesService.convertBinaryStringToBuffer(output), 'xlsx');
  }

  /**
   * Auto sets the column widths of all columns on all sheets.
   * @param {object} workbook The workbook to set Auto column widths on
   */
  private setAutoColumnWidths(workbook: any) {
    let worksheet;
    let columnWidths;

    _.each(workbook.SheetNames, (sheetName: string) => {
      worksheet = workbook.Sheets[sheetName];
      columnWidths = this.getMaximumColumnWidths(worksheet);
      worksheet['!cols'] = Object.keys(columnWidths)
        .sort()
        .map((key) => {
          return { wch: this.roundWidth(columnWidths[key]) + 10 };
        });
    });
  }

  /**
   * Rounds a value up to the nearest 10
   * @param {number} value The input value
   * @returns {number} the value rounded up to the nearest 10
   */
  private roundWidth(value: number) {
    return Math.ceil(value / 10) * 10;
  }

  /**
   * Gets a key value pair of column and maximum width
   * @param {object} worksheet The worksheet to set Auto column widths on
   * @returns {object} the maximum width of each column
   */
  private getMaximumColumnWidths(worksheet: any) {
    let columnName;
    const columnWidths = {};

    _.forIn(worksheet, (value, key) => {
      columnName = key.match(/([A-Z]*)\d*/)[1];
      if (columnName === '' || columnWidths[columnName] >= value.v.toString().length) {
        return;
      }

      columnWidths[columnName] = value.v.length;
    });
    return columnWidths;
  }
}